import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Mail, Send, CheckCircle, AlertTriangle } from 'lucide-react';

const EmailNotificationTest = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [notificationType, setNotificationType] = useState('new_referral');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const notificationTypes = [
    { value: 'new_referral', label: 'New Housing Placement Referral' },
    { value: 'hss_referral_submitted', label: 'New HSS Service Referral' },
    { value: 'referral_status_change', label: 'Referral Status Update' },
    { value: 'hss_referral_status_change', label: 'HSS Referral Status Update' },
    { value: 'payment_failure', label: 'Payment Failure Alert' },
    { value: 'listing_limit_reached', label: 'Listing Limit Reached' },
    { value: 'admin_alert', label: 'Admin Alert' }
  ];

  const getTestData = (type: string) => {
    const baseData = {
      entityId: user?.id || 'test-entity-id',
      entityType: 'test',
      dashboardUrl: `${window.location.origin}/dashboard`,
      recipientName: 'Test User'
    };

    switch (type) {
      case 'new_referral':
        return {
          ...baseData,
          facilityName: 'Test Care Facility',
          clientAge: 75,
          clientGender: 'Female',
          county: 'Hennepin',
          waiverType: 'EW',
          timeline: 'ASAP'
        };

      case 'hss_referral_submitted':
        return {
          ...baseData,
          agencyName: 'Test HSS Agency',
          clientAge: 45,
          clientGender: 'Male',
          county: 'Ramsey',
          housingSituation: 'Homeless',
          supportNeeds: ['Case management', 'Mental health services', 'Housing search assistance']
        };
      
      case 'referral_status_change':
        return {
          ...baseData,
          facilityName: 'Test Care Facility',
          status: 'Accepted',
          isAccepted: true,
          providerNotes: 'We have availability and would be happy to accommodate this client.'
        };

      case 'hss_referral_status_change':
        return {
          ...baseData,
          agencyName: 'Test HSS Agency',
          status: 'Accepted',
          isAccepted: true,
          providerNotes: 'We can provide case management and housing stabilization services for this client.'
        };

      case 'payment_failure':
        return {
          ...baseData,
          providerName: 'Test Provider',
          amount: '150.00',
          failureReason: 'Card declined',
          subscriptionUrl: `${window.location.origin}/subscription`
        };

      case 'listing_limit_reached':
        return {
          ...baseData,
          providerName: 'Test Provider',
          currentLimit: 2,
          subscriptionTier: 'Tier 2',
          subscriptionUrl: `${window.location.origin}/subscription`
        };

      case 'admin_alert':
        return {
          ...baseData,
          alertTitle: 'Test Alert',
          alertType: 'system_test',
          severity: 'medium',
          description: 'This is a test alert to verify the email notification system is working correctly.',
          relatedEntityType: 'notification_test',
          adminDashboardUrl: `${window.location.origin}/dashboard`
        };

      default:
        return baseData;
    }
  };

  const testEmailNotification = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const testData = getTestData(notificationType);
      
      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          type: notificationType,
          recipientEmail: testEmail,
          recipientUserId: user?.id,
          data: testData
        }
      });

      if (error) {
        throw error;
      }

      setTestResult({
        success: true,
        message: `Test email sent successfully! Message ID: ${data?.messageId || 'N/A'}`
      });

      toast({
        title: 'Email Test Successful',
        description: `Test ${notificationType} email sent to ${testEmail}`,
      });

    } catch (error: any) {
      console.error('Email test failed:', error);
      setTestResult({
        success: false,
        message: `Email test failed: ${error.message}`
      });

      toast({
        title: 'Email Test Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Mail className="w-5 h-5" />
          <span>Email Notification System Test</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="test-email">Test Email Address</Label>
            <Input
              id="test-email"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter email to send test notification"
            />
          </div>

          <div>
            <Label htmlFor="notification-type">Notification Type</Label>
            <Select value={notificationType} onValueChange={setNotificationType}>
              <SelectTrigger>
                <SelectValue placeholder="Select notification type" />
              </SelectTrigger>
              <SelectContent>
                {notificationTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-medium mb-2">Test Data Preview:</h4>
            <pre className="text-sm text-muted-foreground whitespace-pre-wrap">
              {JSON.stringify(getTestData(notificationType), null, 2)}
            </pre>
          </div>

          <Button 
            onClick={testEmailNotification}
            disabled={testing || !testEmail}
            className="w-full"
          >
            <Send className="w-4 h-4 mr-2" />
            {testing ? 'Sending Test Email...' : 'Send Test Email'}
          </Button>
        </div>

        {testResult && (
          <Alert className={testResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <div className="flex items-center space-x-2">
              {testResult.success ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-red-600" />
              )}
              <AlertDescription className={testResult.success ? 'text-green-800' : 'text-red-800'}>
                {testResult.message}
              </AlertDescription>
            </div>
          </Alert>
        )}

        <div className="space-y-4">
          <h4 className="font-medium">Email Notification System Status:</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Send-notification function: Ready</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Email templates: Configured</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Resend API: Connected</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Database logging: Enabled</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailNotificationTest;